import { useCallback } from 'react';
import { documentsAPI, type DocumentConversations } from '../../lib/api';
import { useFetchData } from '../../hooks/useFetchData';

export interface UseDocumentConversationsResult {
  /** The viewer's side, whether they may post, and one row per conversation. */
  summary: DocumentConversations | null;
  loading: boolean;
  /** Set when the conversation list could not be loaded. */
  error: string | null;
  /** Re-fetch the list (e.g. to retry after an error). */
  reload: () => Promise<void>;
  /** Zero one conversation's unread count locally once it has been read. */
  markReadLocally: (companyId: number) => void;
}

/**
 * CR-8197: the Communications conversations for a document.
 *
 * Loaded when the detail pop-up opens rather than when the tab is clicked, so
 * the tab label can show the unread count before the user goes there.
 *
 * @example
 * const { summary } = useDocumentConversations(document?.id, open);
 * const unread = summary?.unread_total ?? 0;
 *
 * @param documentId - The open document, or undefined while none is open
 * @param open - Whether the pop-up is open; nothing is fetched while closed
 * @returns The conversation summary plus loading/error state and updaters
 */
export function useDocumentConversations(
  documentId: number | undefined,
  open: boolean,
): UseDocumentConversationsResult {
  const { data, loading, error, setData, refetch } = useFetchData<DocumentConversations | null>(
    async () => {
      if (!open || !documentId) return null;
      const res = await documentsAPI.getDocumentConversations(documentId);
      return res.data;
    },
    { initialLoading: false, deps: [open, documentId] },
  );

  const markReadLocally = useCallback((companyId: number) => {
    setData((prev) => {
      if (!prev) return prev;
      const conversations = prev.conversations.map((c) => (
        c.company_id === companyId ? { ...c, unread_count: 0 } : c
      ));
      const unread_total = conversations.reduce((sum, c) => sum + c.unread_count, 0);
      return { ...prev, conversations, unread_total };
    });
  }, [setData]);

  return { summary: data, loading, error, reload: refetch, markReadLocally };
}
